<template>
  <section class="management-section" data-testid="management-portfolio-risk">
    <ManagementSectionHeader
      :title="$t('UserProjects.management.portfolioRisk.titleMain')"
      :subtitle="$t('UserProjects.management.portfolioRisk.titleSuffix')"
    >
      <template #trailing>
        <span>{{ $t('UserProjects.management.portfolioRisk.ppc') }}</span>
        <i
          v-tooltip.top="$t('UserProjects.management.portfolioRisk.ppcHint')"
          class="pi pi-info-circle text-xs cursor-help"
          data-testid="management-ppc-hint"
        />
      </template>
    </ManagementSectionHeader>

    <div
      v-if="rows.length === 0"
      class="text-sm text-[var(--color-disabled)] py-4"
      data-testid="management-portfolio-risk-empty"
    >
      {{ $t('UserProjects.management.portfolioRisk.empty') }}
    </div>

    <template v-else>
      <div class="management-panel-card overflow-hidden">
        <button
          v-for="(row, index) in paginatedRows"
          :key="row.projectId"
          type="button"
          class="management-portfolio-row w-full text-left flex flex-wrap items-center gap-4 px-5 py-4 transition-colors hover:bg-[#FAFAFA]"
          :class="{ 'border-t border-[var(--color-light-grey)]': index > 0 }"
          :data-testid="`management-portfolio-row-${row.projectId}`"
          @click="$emit('select-project', row.projectId)"
        >
          <span
            class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase shrink-0"
            :style="riskStyle(row.riskLevel)"
          >
            {{ row.riskLevel }}
          </span>

          <div class="flex-1 min-w-[180px]">
            <p class="text-sm font-semibold text-[var(--color-dark)] m-0 truncate">{{ row.projectName }}</p>
            <p class="text-xs text-[var(--color-disabled)] m-0 truncate">{{ row.projectLeadName }}</p>
          </div>

          <div class="text-center shrink-0">
            <p class="text-lg font-bold text-[var(--color-dark)] m-0">{{ formatPpc(row.ppcPercent) }}</p>
            <p
              class="text-[11px] m-0 flex items-center gap-0.5 justify-center"
              :class="trendClass(row.ppcDeltaPercent)"
            >
              <i :class="trendIcon(row.ppcDeltaPercent)" class="text-xs" />
              {{ formatTrend(row.ppcDeltaPercent) }}
            </p>
          </div>

          <div class="text-center shrink-0 w-20">
            <p class="text-[15px] font-bold text-[var(--color-dark)] m-0">{{ row.activeConstraintsCount }}</p>
            <p class="text-[10px] text-[var(--color-disabled)] uppercase tracking-wider m-0">
              {{ $t('UserProjects.management.portfolioRisk.constraints') }}
            </p>
          </div>

          <div class="text-center shrink-0 w-20">
            <p
              class="text-[15px] font-bold m-0"
              :style="{ color: openDecisionsColor(row.openDecisionsCount) }"
            >
              {{ row.openDecisionsCount }}
            </p>
            <p class="text-[10px] text-[var(--color-disabled)] uppercase tracking-wider m-0">
              {{ $t('UserProjects.management.portfolioRisk.decisions') }}
            </p>
          </div>

          <div class="shrink-0 min-w-[140px] text-right">
            <template v-if="row.criticalFocusLabel">
              <p class="text-xs font-medium text-[var(--color-dark)] m-0">{{ row.criticalFocusLabel }}</p>
              <p v-if="row.criticalFocusDate" class="text-[11px] text-[var(--color-disabled)] m-0">
                {{ row.criticalFocusDate }}
              </p>
            </template>
            <span v-else class="text-[var(--color-disabled)]">—</span>
          </div>
        </button>
      </div>

      <div
        v-if="showPaginator"
        class="flex justify-end mt-3"
        data-testid="management-portfolio-risk-paginator"
      >
        <Paginator
          :first="first"
          :rows="pageSize"
          :total-records="rows.length"
          :rows-per-page-options="pageSizeOptions"
          @page="onPage"
        />
      </div>
    </template>
  </section>
</template>

<script>
import Paginator from 'primevue/paginator';
import ManagementSectionHeader from '@/components/CustomerApp/UserProjects/ManagementSectionHeader.vue';
import {
  clampPortfolioRiskPageSize,
  formatPpcTrend,
  normalizePortfolioRiskPaginatorFirst,
  openDecisionsColor,
  paginateArray,
  PORTFOLIO_RISK_PAGE_SIZE_DEFAULT,
  PORTFOLIO_RISK_PAGE_SIZE_OPTIONS,
  PORTFOLIO_RISK_STYLES,
} from '@/utils/managementDisplay';

export default {
  name: 'ManagementPortfolioRiskTable',
  components: { ManagementSectionHeader, Paginator },
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['select-project'],
  data() {
    return {
      first: 0,
      pageSize: PORTFOLIO_RISK_PAGE_SIZE_DEFAULT,
      pageSizeOptions: PORTFOLIO_RISK_PAGE_SIZE_OPTIONS,
    };
  },
  computed: {
    paginatedRows() {
      return paginateArray(this.rows, this.first, this.pageSize);
    },
    showPaginator() {
      return this.rows.length > 0;
    },
  },
  watch: {
    rows() {
      this.first = normalizePortfolioRiskPaginatorFirst(this.first, this.rows.length, this.pageSize);
    },
  },
  methods: {
    openDecisionsColor,
    onPage(event) {
      this.pageSize = clampPortfolioRiskPageSize(event.rows);
      this.first = normalizePortfolioRiskPaginatorFirst(event.first, this.rows.length, this.pageSize);
    },
    riskStyle(level) {
      return PORTFOLIO_RISK_STYLES[level] ?? PORTFOLIO_RISK_STYLES.NIEDRIG;
    },
    formatPpc(value) {
      if (value === null || value === undefined) return '—';
      return `${Math.round(value)}%`;
    },
    formatTrend(delta) {
      return formatPpcTrend(delta).text;
    },
    trendIcon(delta) {
      const { direction } = formatPpcTrend(delta);
      if (direction === 'down') return 'pi pi-arrow-down';
      if (direction === 'up') return 'pi pi-arrow-up';
      return 'pi pi-minus';
    },
    trendClass(delta) {
      const { direction } = formatPpcTrend(delta);
      if (direction === 'up') return 'text-[var(--color-success-text)]';
      if (direction === 'down') return 'text-[var(--color-error-text)]';
      return 'text-[var(--color-disabled)]';
    },
  },
};
</script>

<style scoped>
@import './management-section.css';
</style>
